// Import the actual config modules
import merge from 'lodash/merge';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import developmentConfig from '../development';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import localhostConfig from '../localhost';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import type config from '../production';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import productionConfig from '../production';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import stagingConfig from '../staging';

export type ConfigType = typeof config;

const PROD_DOMAINS = [
  'dash.newfront.com',
  'eks-dash.newfront.com',
  'app.newfront.com',
  'eks-app.newfront.com',
  'legacy.newfront.com',
  'navigator.wtwco.com',
  'nova.wtwco.com',
];

const BOX_FRONTEND_HOST_SUFFIXES = ['.dash.local.internal', '.app.local.internal'];

const LOCAL_BACKEND_HOST = 'backend.local.internal';
const LOCAL_BACKEND_PROTOCOL = 'https:';

const LOCALHOST_DOMAINS = ['dash.local.internal', 'app.local.internal', 'nova.local.internal', 'localhost'];

const STAGING_DOMAINS = [
  'dash.newfront-staging.com',
  'app.newfront-staging.com',
  'stage-legacy.newfront-development.com',
  'stage-navigator.wtwco.com',
  'stage-nova.wtwco.com',
];

const STAGING_FRONTEND_HOST_SUFFIXES = ['.preview.newfront-development.com'];

export function getEnvFromUrl(): 'stage' | 'prod' | 'dev' | 'localhost' | undefined {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  const urlParts = url.hostname.split('.');
  const subdomain = urlParts[0];
  const mainDomain = urlParts[1];

  if (subdomain.startsWith('dev')) {
    return 'dev';
  }

  if (
    mainDomain?.startsWith('ngrok') ||
    LOCALHOST_DOMAINS.includes(url.hostname) ||
    BOX_FRONTEND_HOST_SUFFIXES.some((suffix) => url.hostname.endsWith(suffix))
  ) {
    return 'localhost';
  }

  if (
    subdomain.startsWith('stage') ||
    STAGING_DOMAINS.includes(url.hostname) ||
    STAGING_FRONTEND_HOST_SUFFIXES.some((suffix) => url.hostname.endsWith(suffix))
  ) {
    return 'stage';
  }

  if (subdomain.startsWith('prod') || PROD_DOMAINS.includes(url.hostname)) {
    return 'prod';
  }
}

function getDevBoxUrl(envUrl?: string): string | undefined {
  if (envUrl) {
    return envUrl.replace(/\/+$/, '');
  }

  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);

  const matchedSuffix = BOX_FRONTEND_HOST_SUFFIXES.find((suffix) => url.hostname.endsWith(suffix));

  if (!matchedSuffix) {
    return;
  }

  const boxId = url.hostname.slice(0, -matchedSuffix.length);

  return `${url.protocol}//${boxId}.backend.local.internal`;
}

function rewriteLocalBackendUrl(value: string, boxUrl: string): string {
  let apiUrl: URL;
  let devBoxUrl: URL;

  try {
    apiUrl = new URL(value);
    devBoxUrl = new URL(boxUrl);
  } catch {
    return value;
  }

  if (apiUrl.protocol !== LOCAL_BACKEND_PROTOCOL || apiUrl.hostname !== LOCAL_BACKEND_HOST) {
    return value;
  }

  const hasExplicitPath = value.slice(`${apiUrl.protocol}//${apiUrl.host}`.length).startsWith('/');
  const pathname = hasExplicitPath ? apiUrl.pathname : '';

  return `${devBoxUrl.origin}${pathname}${apiUrl.search}${apiUrl.hash}`;
}

function withDevBoxConfig<TConfig extends ConfigType>(baseConfig: TConfig, devBoxEnvUrl?: string): TConfig {
  const boxUrl = getDevBoxUrl(devBoxEnvUrl);

  if (!boxUrl) {
    return baseConfig;
  }

  return {
    ...baseConfig,
    API: Object.entries(baseConfig.API).reduce(
      (api, [key, value]) => ({
        ...api,
        [key]: typeof value === 'string' ? rewriteLocalBackendUrl(value, boxUrl) : value,
      }),
      {},
    ) as ConfigType['API'],
  };
}

export interface GetConfigOptions<TCustomConfig = Record<string, unknown>> {
  customConfigs?: {
    production?: TCustomConfig;
    staging?: TCustomConfig;
    localhost?: TCustomConfig;
    development?: TCustomConfig;
  };
  deepMerge?: boolean;
}

/**
 * Get configuration for the current environment.
 * Handles all environments (prod, stage, localhost, dev) with optional custom config merging.
 *
 * @param options - Optional configuration options
 * @param options.customConfigs - Custom configurations to merge for each environment
 * @param options.deepMerge - Whether to use deep merge (requires lodash/merge) or shallow merge
 * @returns The configuration object for the current environment
 */
export function getConfig<TCustomConfig = Record<string, unknown>>(
  options: GetConfigOptions<TCustomConfig> = {},
): ConfigType & TCustomConfig {
  /* eslint-disable no-process-env */
  const env = process.env.NEWFRONT_ENV || getEnvFromUrl();
  const devBoxEnvUrl = process.env.APP_CRAFTER_BOX_URL;
  const { customConfigs, deepMerge = false } = options;

  let baseConfig: ConfigType;
  let customConfig: TCustomConfig | undefined;

  /**
   * We handle the config this way so that Webpack will tree-shake the unused config
   * from the final build on the client.
   */
  if (env === 'prod') {
    baseConfig = productionConfig;
    customConfig = customConfigs?.production;
  } else if (env === 'stage') {
    baseConfig = stagingConfig;
    customConfig = customConfigs?.staging;
  } else if (env === 'localhost') {
    baseConfig = localhostConfig;
    customConfig = customConfigs?.localhost;
  } else {
    baseConfig = developmentConfig;
    customConfig = customConfigs?.development;
  }

  // If no custom config, return base config
  if (!customConfig) {
    return withDevBoxConfig(baseConfig as ConfigType & TCustomConfig, devBoxEnvUrl);
  }

  if (deepMerge) {
    try {
      return withDevBoxConfig(merge({}, baseConfig, customConfig), devBoxEnvUrl);
    } catch (error) {
      return withDevBoxConfig({ ...baseConfig, ...customConfig } as ConfigType & TCustomConfig, devBoxEnvUrl);
    }
  }

  return withDevBoxConfig({ ...baseConfig, ...customConfig } as ConfigType & TCustomConfig, devBoxEnvUrl);
}
